import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FirstClassRecipeBaseActions } from './first-class-recipe-base.state';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { FirstClassRecipeBaseApi } from './first-class-recipe-base.api';
import { UserEntity } from '../user-base';

@Injectable()
export class FirstClassRecipeBaseEffect {

  #actions = inject(Actions);
  #api = inject(FirstClassRecipeBaseApi);
  #userEntity = inject(UserEntity);

  // LOGSTASH EFFECTS
  onDeployGlobalElasticStackWithLogstash$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployGlobalElasticStackWithLogstash),
    map(({ sourceProjectId, seriousCore, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        forwardLogsFromProjectId: sourceProjectId,
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        projectCorePackage: seriousCore ? 'SERIOUS' : 'LIGHT',
        includeLogstash: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackWithLogstash.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackWithLogstash.type}-${sourceProjectId}` }
      }
    ))
  ));

  onDeployGlobalElasticStackAndForward$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployGlobalElasticStackAndForward),
    map(({ sourceProjectId, seriousCore, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        forwardLogsFromProjectId: sourceProjectId,
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        projectCorePackage: seriousCore ? 'SERIOUS' : 'LIGHT',
        includeLogstash: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackAndForward.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackAndForward.type}-${sourceProjectId}` }
      }
    ))
  ));

  onDeployDedicatedElasticStackWithLogstash$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployDedicatedElasticStackWithLogstash),
    map(({ sourceProjectId, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elkProjectId: sourceProjectId,
        forwardLogsFromProjectId: sourceProjectId,
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        includeLogstash: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackWithLogstash.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackWithLogstash.type}-${sourceProjectId}` }
      }
    ))
  ));

  onDeployDedicatedElasticStackAndForward$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployDedicatedElasticStackAndForward),
    map(({ sourceProjectId, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elkProjectId: sourceProjectId,
        forwardLogsFromProjectId: sourceProjectId,
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        includeLogstash: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackAndForward.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackAndForward.type}-${sourceProjectId}` }
      }
    ))
  ));

  onForwardToGlobalElasticStack$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.forwardToGlobalElasticStack),
    map(({ sourceProjectId, targetProjectId }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elkProjectId: targetProjectId,
        forwardLogsFromProjectId: sourceProjectId,
        includeLogstash: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.forwardToGlobalElasticStack.type}-${targetProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.forwardToGlobalElasticStack.type}-${targetProjectId}` }
      }
    ))
  ));

  // APM EFFECTS
  onDeployGlobalElasticStackWithApm$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployGlobalElasticStackWithAPM),
    map(({ sourceProjectId, seriousCore, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        projectCorePackage: seriousCore ? 'SERIOUS' : 'LIGHT',
        includeApm: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackWithAPM.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalElasticStackWithAPM.type}-${sourceProjectId}` }
      }
    ))
  ));

  onDeployDedicatedElasticStackWithApm$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployDedicatedElasticStackWithAPM),
    map(({ sourceProjectId, ha }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elkProjectId: sourceProjectId,
        elasticsearchMode: ha ? 'HA' : 'NON_HA',
        includeApm: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackWithAPM.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedElasticStackWithAPM.type}-${sourceProjectId}` }
      }
    ))
  ));

  onAddApmToElasticStack$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.addAPMToElasticStack),
    map(({ elkProjectId }) => FirstClassRecipeBaseActions.firstClassElasticStack(
      {
        elkProjectId,
        includeApm: true
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.addAPMToElasticStack.type}-${elkProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.addAPMToElasticStack.type}-${elkProjectId}` }
      }
    ))
  ));

  // GENERIC
  onFirstClassElasticStackRequest$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.firstClassElasticStack),
    withLatestFrom(this.#userEntity.activeClientId$),
    switchMap(([ action, clientId ]) => {
      return this.#api.deploySetupElk$({
        clientId,
        ...action.data
      }).pipe(
        map((res) => FirstClassRecipeBaseActions.firstClassElasticStackSuccess(res, action)),
        catchError((err) => of(FirstClassRecipeBaseActions.firstClassElasticStackFail(err, action)))
      );
    })
  ));

  // PROMETHEUS EFFECTS
  onDeployGlobalPrometheus$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployGlobalPrometheus),
    map(({ sourceProjectId, seriousCore, ha }) => FirstClassRecipeBaseActions.firstClassPrometheus(
      {
        forwardMetricsFromProjectId: sourceProjectId,
        grafanaDatabaseMode: ha ? 'HA' : 'NON_HA',
        projectCorePackage: seriousCore ? 'SERIOUS' : 'LIGHT'
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalPrometheus.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployGlobalPrometheus.type}-${sourceProjectId}` }
      }
    ))
  ));

  onDeployDedicatedPrometheus$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.deployDedicatedPrometheus),
    map(({ sourceProjectId, ha }) => FirstClassRecipeBaseActions.firstClassPrometheus(
      {
        prometheusProjectId: sourceProjectId,
        forwardMetricsFromProjectId: sourceProjectId,
        grafanaDatabaseMode: ha ? 'HA' : 'NON_HA'
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedPrometheus.type}-${sourceProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.deployDedicatedPrometheus.type}-${sourceProjectId}` }
      }
    ))
  ));

  onForwardToGlobalPrometheus$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.forwardToGlobalPrometheus),
    map(({ sourceProjectId, targetProjectId, ha }) => FirstClassRecipeBaseActions.firstClassPrometheus(
      {
        prometheusProjectId: targetProjectId,
        forwardMetricsFromProjectId: sourceProjectId,
        grafanaDatabaseMode: ha ? 'HA' : 'NON_HA'
      },
      {
        type: 'snack',
        meta: { tag: `${FirstClassRecipeBaseActions.forwardToGlobalPrometheus.type}-${targetProjectId}` }
      },
      {
        meta: { tag: `${FirstClassRecipeBaseActions.forwardToGlobalPrometheus.type}-${targetProjectId}` }
      }
    ))
  ));

  onFirstClassPrometheusRequest$ = createEffect(() => this.#actions.pipe(
    ofType(FirstClassRecipeBaseActions.firstClassPrometheus),
    withLatestFrom(this.#userEntity.activeClientId$),
    switchMap(([ action, clientId ]) => {
      return this.#api.deploySetupPrometheus$({
        clientId,
        ...action.data
      }).pipe(
        map((res) => FirstClassRecipeBaseActions.firstClassPrometheusSuccess(res, action)),
        catchError((err) => of(FirstClassRecipeBaseActions.firstClassPrometheusFail(err, action)))
      );
    })
  ));

}
